import { z } from 'zod';
import type { MembershipGrant, PermissionSet, Role } from '../rbac.ts';
import { localeSchema, patchSchema, type Locale } from './common.ts';

export const loginSchema = z.object({
  login: z.string().trim().min(1, 'Loginni kiriting').max(64),
  password: z.string().min(1, 'Parolni kiriting').max(256),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8, "Parol kamida 8 belgidan iborat bo'lishi kerak").max(256),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/** Interface settings ("Interfeys sozlamalari") persisted per user. */
export const userSettingsSchema = z.object({
  locale: localeSchema.default('uz-Latn'),
  theme: z.enum(['dark', 'light', 'system']).default('dark'),
  /** "Asosiy rang" */
  accentColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .default('#0275FF'),
  /** "Matn o'lchami" — base text size in design px; scales the whole UI proportionally */
  fontSize: z.union([z.literal(12), z.literal(13), z.literal(14), z.literal(15), z.literal(16)]).default(14),
  compactTables: z.boolean().default(false),
  animations: z.boolean().default(true),
  sidebarCollapsed: z.boolean().default(false),
  /** action id → key combo, e.g. { "save": "Ctrl+S" } */
  hotkeys: z.record(z.string(), z.string()).default({}),
});
export type UserSettings = z.infer<typeof userSettingsSchema>;
export const updateUserSettingsSchema = patchSchema(userSettingsSchema);

export interface MeUser {
  id: string;
  login: string;
  fullName: string;
  position: string | null;
  phone: string | null;
  avatarUrl: string | null;
  locale: Locale;
  theme: UserSettings['theme'];
  settings: UserSettings;
}

export interface AccessibleOrganization {
  id: string;
  name: string;
  slug: string;
  isAdmin: boolean;
}

export interface AccessibleTenant {
  id: string;
  organizationId: string;
  name: string;
  slug: string;
  kind: string;
  isAdmin: boolean;
  role: Role;
  permissions: PermissionSet;
}

export interface MeResponse {
  user: MeUser;
  isPlatformAdmin: boolean;
  memberships: MembershipGrant[];
  organizations: AccessibleOrganization[];
  tenants: AccessibleTenant[];
}
